import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { RagFileUploadState, fetchRagFiles } from '@/api/content/file'
import type { RagFile } from '@/api/content/file'
import {
  clearPresentationDefaultTemplate,
  clearProposalDefaultTemplate,
  getPresentationDefaultTemplate,
  getProposalDefaultTemplate,
  setPresentationDefaultTemplate,
  setProposalDefaultTemplate
} from '@/api/documentAgent/documentAgent'
import { contentSearchSchema } from '@/common/contentSearchSchema'
import { RagFilePage } from '@/components/RagFilePage'
import { isProposalDocFile } from '@/components/proposal/proposalFiles'

export const Route = createFileRoute('/_auth/$orgSlug/content/file/')({
  validateSearch: contentSearchSchema,
  loaderDeps: ({ search }) => ({ page: search.page, q: search.q, docId: search.docId }),
  loader: async ({ deps, context }) => {
    const proposalEnabled = context.user.userOrgProfile?.enabled_features.proposal_agent ?? false
    const presentationEnabled = context.user.userOrgProfile?.enabled_features.presentation_agent ?? false
    const [files, proposalDefaultTemplate, presentationDefaultTemplate] = await Promise.all([
      fetchRagFiles({ currentPage: deps.page, searchText: deps.q, docId: deps.docId }),
      proposalEnabled ? getProposalDefaultTemplate() : Promise.resolve({ template: null }),
      presentationEnabled ? getPresentationDefaultTemplate() : Promise.resolve({ template: null })
    ])
    return {
      ...files,
      proposalEnabled,
      presentationEnabled,
      proposalDefaultTemplate: proposalDefaultTemplate.template,
      presentationDefaultTemplate: presentationDefaultTemplate.template
    }
  },
  head: () => ({ meta: [{ title: 'Global Content - Snowvault' }] }),
  component: FilePage
})

function FilePage() {
  const {
    items,
    pageCount,
    documentCount,
    proposalEnabled,
    presentationEnabled,
    proposalDefaultTemplate,
    presentationDefaultTemplate
  } = Route.useLoaderData()
  const { userOrgProfile } = Route.useRouteContext().user
  const { page, q, docId } = Route.useSearch()
  const navigate = Route.useNavigate()

  useEffect(() => {
    if (items.length === 0 && page > 1) {
      void navigate({ search: { page: 1, q } })
    }
  }, [items.length, page, q, navigate])

  return (
    <RagFilePage
      breadcrumbs={[{ label: 'Global Content' }, { label: 'Files' }]}
      items={items}
      currentPage={page}
      pageCount={pageCount}
      documentCount={documentCount}
      searchText={q}
      onPageChange={(p) => void navigate({ search: { page: p, q } })}
      onSearchChange={(text) => void navigate({ search: { q: text, page: 1 } })}
      onClearDocId={docId !== undefined ? () => void navigate({ search: { page: 1, q: '' } }) : undefined}
      assignmentControls={[
        ...(proposalEnabled
          ? [
              {
                assignedDocId: proposalDefaultTemplate?.docId ?? null,
                label: 'Default proposal template',
                canManage: userOrgProfile?.org.admin === true,
                canAssign: (item: RagFile) =>
                  item.fileUploadState === RagFileUploadState.Complete && isProposalDocFile(item.fileName),
                onAssign: async (fileId: number) => {
                  await setProposalDefaultTemplate(fileId)
                },
                onClear: clearProposalDefaultTemplate
              }
            ]
          : []),
        ...(presentationEnabled
          ? [
              {
                assignedDocId: presentationDefaultTemplate?.docId ?? null,
                label: 'Default presentation template',
                canManage: userOrgProfile?.org.admin === true,
                canAssign: (item: RagFile) =>
                  item.fileUploadState === RagFileUploadState.Complete && item.fileName.toLowerCase().endsWith('.pptx'),
                onAssign: async (fileId: number) => {
                  await setPresentationDefaultTemplate(fileId)
                },
                onClear: clearPresentationDefaultTemplate
              }
            ]
          : [])
      ]}
    />
  )
}
